(() => {
  const HEADER_CLASS = 'hartara-home-header-overlay';

  class HartaraHero extends HTMLElement {
    connectedCallback() {
      this.overlayHeader = this.dataset.overlayHeader === 'true';
      if (!this.overlayHeader) return;

      document.body.classList.add(HEADER_CLASS);
      this.syncHeaderOffset = this.syncHeaderOffset.bind(this);
      this.syncHeaderOffset();
      window.addEventListener('resize', this.syncHeaderOffset, { passive: true });

      if ('ResizeObserver' in window) {
        this.headerObserver = new ResizeObserver(this.syncHeaderOffset);
        document.querySelectorAll('.shopify-section-group-header-group').forEach((element) => {
          this.headerObserver.observe(element);
        });
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.syncHeaderOffset);
      this.headerObserver?.disconnect();
      if (!document.querySelector('hartara-hero[data-overlay-header="true"]')) {
        document.body.classList.remove(HEADER_CLASS);
        document.documentElement.style.removeProperty('--hartara-header-offset');
      }
    }

    syncHeaderOffset() {
      const groups = [...document.querySelectorAll('.shopify-section-group-header-group')];
      const height = groups.reduce((total, element) => {
        const styles = window.getComputedStyle(element);
        if (styles.display === 'none') return total;
        return total + element.getBoundingClientRect().height;
      }, 0);
      document.documentElement.style.setProperty('--hartara-header-offset', `${Math.round(height)}px`);
    }
  }

  class HartaraCarousel extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-carousel-viewport]');
      this.track = this.querySelector('[data-carousel-track]');
      this.prevButton = this.querySelector('[data-carousel-prev]');
      this.nextButton = this.querySelector('[data-carousel-next]');
      this.progress = this.querySelector('[data-carousel-progress]');

      if (!this.viewport || !this.track || this.dataset.carouselReady === 'true') return;

      this.dataset.carouselReady = 'true';
      this.activePointerId = null;
      this.dragStartX = 0;
      this.dragStartScrollLeft = 0;
      this.hasDragged = false;
      this.suppressClickUntil = 0;

      this.onScroll = this.update.bind(this);
      this.onResize = this.update.bind(this);
      this.onPrevClick = () => this.move(-1);
      this.onNextClick = () => this.move(1);
      this.onPointerDownBound = this.onPointerDown.bind(this);
      this.onPointerMoveBound = this.onPointerMove.bind(this);
      this.onPointerUpBound = this.onPointerUp.bind(this);
      this.onClickCaptureBound = this.onClickCapture.bind(this);
      this.onDragStartBound = this.onDragStart.bind(this);

      this.prevButton?.addEventListener('click', this.onPrevClick);
      this.nextButton?.addEventListener('click', this.onNextClick);
      this.viewport.addEventListener('scroll', this.onScroll, { passive: true });
      this.viewport.addEventListener('pointerdown', this.onPointerDownBound);
      this.viewport.addEventListener('pointermove', this.onPointerMoveBound);
      this.viewport.addEventListener('pointerup', this.onPointerUpBound);
      this.viewport.addEventListener('pointercancel', this.onPointerUpBound);
      this.viewport.addEventListener('click', this.onClickCaptureBound, true);
      this.viewport.addEventListener('dragstart', this.onDragStartBound);
      window.addEventListener('resize', this.onResize, { passive: true });

      requestAnimationFrame(() => this.update());
    }

    disconnectedCallback() {
      this.prevButton?.removeEventListener('click', this.onPrevClick);
      this.nextButton?.removeEventListener('click', this.onNextClick);
      this.viewport?.removeEventListener('scroll', this.onScroll);
      this.viewport?.removeEventListener('pointerdown', this.onPointerDownBound);
      this.viewport?.removeEventListener('pointermove', this.onPointerMoveBound);
      this.viewport?.removeEventListener('pointerup', this.onPointerUpBound);
      this.viewport?.removeEventListener('pointercancel', this.onPointerUpBound);
      this.viewport?.removeEventListener('click', this.onClickCaptureBound, true);
      this.viewport?.removeEventListener('dragstart', this.onDragStartBound);
      window.removeEventListener('resize', this.onResize);
    }

    cardStep() {
      const firstCard = this.track?.firstElementChild;
      if (!firstCard) return this.viewport.clientWidth;

      const gap = parseFloat(window.getComputedStyle(this.track).gap) || 0;
      return firstCard.getBoundingClientRect().width + gap;
    }

    move(direction) {
      this.viewport.scrollBy({
        left: direction * this.cardStep(),
        behavior: 'smooth',
      });
    }

    onPointerDown(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;

      const maxScroll = this.viewport.scrollWidth - this.viewport.clientWidth;
      if (maxScroll <= 2) return;

      this.activePointerId = event.pointerId;
      this.dragStartX = event.clientX;
      this.dragStartScrollLeft = this.viewport.scrollLeft;
      this.hasDragged = false;
    }

    onPointerMove(event) {
      if (event.pointerId !== this.activePointerId) return;

      const distance = event.clientX - this.dragStartX;

      if (!this.hasDragged && Math.abs(distance) < 6) return;

      if (!this.hasDragged) {
        this.hasDragged = true;
        this.viewport.classList.add('is-dragging');
        this.viewport.setPointerCapture?.(event.pointerId);
      }

      event.preventDefault();
      this.viewport.scrollLeft = this.dragStartScrollLeft - distance;
    }

    onPointerUp(event) {
      if (event.pointerId !== this.activePointerId) return;

      if (this.hasDragged) {
        this.suppressClickUntil = performance.now() + 250;
      }

      if (this.hasDragged && this.viewport.hasPointerCapture?.(event.pointerId)) {
        this.viewport.releasePointerCapture(event.pointerId);
      }

      this.activePointerId = null;
      this.hasDragged = false;
      this.viewport.classList.remove('is-dragging');
      this.update();
    }

    onClickCapture(event) {
      if (performance.now() >= this.suppressClickUntil) return;

      event.preventDefault();
      event.stopPropagation();
    }

    onDragStart(event) {
      if (event.target.closest('img, a')) {
        event.preventDefault();
      }
    }

    update() {
      const maxScroll = Math.max(0, this.viewport.scrollWidth - this.viewport.clientWidth);
      const current = Math.min(maxScroll, Math.max(0, this.viewport.scrollLeft));
      const atStart = current <= 2;
      const atEnd = maxScroll - current <= 2;

      if (this.prevButton) this.prevButton.disabled = atStart;
      if (this.nextButton) this.nextButton.disabled = atEnd;

      if (this.progress) {
        const percentage = maxScroll === 0 ? 100 : 18 + (82 * current) / maxScroll;
        this.progress.style.width = `${Math.min(100, percentage)}%`;
      }
    }
  }

  class HartaraVideoModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.openButton = this.querySelector('[data-video-open]');
      this.closeButton = this.querySelector('[data-video-close]');
      this.video = this.dialog?.querySelector('video');

      if (!this.dialog || !this.openButton) return;

      this.openButton.addEventListener('click', () => this.open());
      this.closeButton?.addEventListener('click', () => this.close());
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });
      this.dialog.addEventListener('close', () => this.pause());
    }

    open() {
      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }
      this.video?.play().catch(() => {});
    }

    close() {
      if (typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
      }
      this.pause();
    }

    pause() {
      if (!this.video) return;
      this.video.pause();
      this.video.currentTime = 0;
    }
  }

  if (!customElements.get('hartara-hero')) customElements.define('hartara-hero', HartaraHero);
  if (!customElements.get('hartara-carousel')) customElements.define('hartara-carousel', HartaraCarousel);
  if (!customElements.get('hartara-video-modal')) customElements.define('hartara-video-modal', HartaraVideoModal);
})();
